/**
 * 지출결의서 양식 HWP 원문 (API / IndexedDB)
 */
import { isBdApiEnabled, FILE_BUCKET } from 'services/pocRowPersist';
import { uploadAppFile, downloadAppFile, deleteAppFile } from 'services/appFileApi';

const DB_NAME = 'erp.expense.resolution.form';
const DB_VERSION = 1;
const STORE = 'files';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB를 사용할 수 없습니다.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'resolutionUid' });
      }
    };
  });
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
  });
}

function toBytes(hwpBytes) {
  if (!hwpBytes) return null;
  if (hwpBytes instanceof ArrayBuffer) return new Uint8Array(hwpBytes);
  if (hwpBytes instanceof Uint8Array) return hwpBytes;
  return new Uint8Array(hwpBytes);
}

export async function saveResolutionFormHwp(resolutionUid, hwpBytes, fileName = 'resolution.hwp') {
  const uid = String(resolutionUid || '').trim();
  if (!uid) throw new Error('resolutionUid가 필요합니다.');
  const bytes = toBytes(hwpBytes);
  if (!bytes?.length) throw new Error('HWP 바이트가 없습니다.');
  if (isBdApiEnabled()) {
    await uploadAppFile({
      bucket: FILE_BUCKET.RESOLUTION_FORM,
      objectKey: uid,
      file: new Blob([bytes], { type: 'application/octet-stream' }),
      fileName,
    });
    return;
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(
      tx.objectStore(STORE).put({
        resolutionUid: uid,
        fileName,
        hwpBytes: bytes,
        updatedAt: new Date().toISOString(),
      }),
    );
  } finally {
    db.close();
  }
}

export async function getResolutionFormHwp(resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  if (!uid) return null;
  if (isBdApiEnabled()) {
    const blob = await downloadAppFile(FILE_BUCKET.RESOLUTION_FORM, uid);
    if (!blob) return null;
    const hwpBytes = new Uint8Array(await blob.arrayBuffer());
    return { resolutionUid: uid, fileName: 'resolution.hwp', hwpBytes, updatedAt: '' };
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    return (await idbReq(tx.objectStore(STORE).get(uid))) || null;
  } finally {
    db.close();
  }
}

export async function deleteResolutionFormHwp(resolutionUid) {
  const uid = String(resolutionUid || '').trim();
  if (!uid) return;
  if (isBdApiEnabled()) {
    await deleteAppFile(FILE_BUCKET.RESOLUTION_FORM, uid);
    return;
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(tx.objectStore(STORE).delete(uid));
  } finally {
    db.close();
  }
}
